import { useEffect, useState } from "react";
import { View, Text, TouchableOpacity, ActivityIndicator, Platform } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { getItem } from "../app/lib/storage";

const API_URL = (process.env.EXPO_PUBLIC_API_URL || "https://dashhound.app") + "/api";
const IS_WEB = Platform.OS === "web";

interface YearStat {
  year: string;
  performance: {
    runCount: number;
    bestSpeed: number;
    bestTime: number;
    bestThreeAvgSpeed: number;
    totalPoints: number;
    titles: string[];
  } | null;
  ranking: {
    rank: number;
    totalInBreed: number;
    speed: number | null;
    akcDogName: string;
    top5: Array<{ rank: number; dogName: string; speed: number | null }>;
  } | null;
}

interface Props {
  dogId: string;
  hasRegisteredName: boolean;
  /** Skip the current year (already shown in the BreedStandings card above). */
  currentYear?: string;
  onAddRegisteredName?: () => void;
  /** Use the public (no-auth) endpoint when rendered on a shared dog profile. */
  publicMode?: boolean;
  /** Auth token, only needed when publicMode is false on native. */
  authToken?: string | null;
}

export function HistoricalRankings({ dogId, hasRegisteredName, currentYear, onAddRegisteredName, publicMode = false, authToken }: Props) {
  const [data, setData] = useState<YearStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [openYear, setOpenYear] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const endpoint = publicMode
        ? `${API_URL}/public/dogs/${dogId}/historical-rankings`
        : `${API_URL}/dogs/${dogId}/historical-rankings`;
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      // Native needs explicit Bearer token; web uses cookie-based auth via credentials: include
      if (!publicMode && !IS_WEB) {
        const token = authToken ?? (await getItem("accessToken"));
        if (token) headers["Authorization"] = `Bearer ${token}`;
      }
      try {
        const res = await fetch(endpoint, {
          headers,
          ...(IS_WEB && !publicMode ? { credentials: "include" as RequestCredentials } : {}),
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const rows: YearStat[] = await res.json();
        if (cancelled) return;
        const filtered = currentYear ? rows.filter((r) => r.year !== currentYear) : rows;
        setData(filtered);
      } catch {
        if (!cancelled) setData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dogId, currentYear, publicMode, authToken]);

  if (loading) {
    return (
      <View style={{ backgroundColor: "#fff", borderRadius: 14, padding: 20, marginBottom: 16, borderWidth: 1, borderColor: "#e5e7eb", alignItems: "center" }}>
        <ActivityIndicator color="#1B2A4A" />
      </View>
    );
  }

  if (data.length === 0) {
    if (!hasRegisteredName) {
      return (
        <TouchableOpacity
          onPress={onAddRegisteredName}
          style={{
            backgroundColor: "#FFF7E6",
            borderLeftWidth: 3,
            borderLeftColor: "#F5A623",
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
          }}
        >
          <Text style={{ fontSize: 13, color: "#1B2A4A", fontWeight: "700", marginBottom: 2 }}>
            See your dog's historical AKC rankings
          </Text>
          <Text style={{ fontSize: 12, color: "#6b7280" }}>
            Add your AKC registered name on Edit Dog to unlock previous-year rank, speed, and standings.
          </Text>
        </TouchableOpacity>
      );
    }
    return null;
  }

  return (
    <View style={{ backgroundColor: "#fff", borderRadius: 14, marginBottom: 16, borderWidth: 1, borderColor: "#e5e7eb", overflow: "hidden" }}>
      <View style={{ paddingHorizontal: 16, paddingTop: 14, paddingBottom: 8 }}>
        <Text style={{ fontSize: 16, fontWeight: "800", color: "#1B2A4A" }}>Historical Stats & Rankings</Text>
        <Text style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>Tap a year to see performance and AKC standings</Text>
      </View>

      {data.map((row) => {
        const isOpen = openYear === row.year;
        const hasPerf = row.performance && row.performance.runCount > 0;
        const hasRank = !!row.ranking;
        // Display label — capitalize "lifetime" to "Lifetime" but pass numeric years through unchanged
        const yearLabel = row.year === "lifetime" ? "Lifetime" : row.year;
        return (
          <View key={row.year}>
            <TouchableOpacity
              onPress={() => setOpenYear(isOpen ? null : row.year)}
              style={{
                paddingHorizontal: 16,
                paddingVertical: 14,
                borderTopWidth: 1,
                borderTopColor: "#f3f4f6",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: isOpen ? "#F4F6F8" : "#fff",
              }}
            >
              <View style={{ flexDirection: "row", alignItems: "center", gap: 8, flex: 1 }}>
                <Text style={{ fontSize: 16, fontWeight: "800", color: "#1B2A4A" }}>{yearLabel}</Text>
                {hasRank && (
                  <View style={{ backgroundColor: "#F5A623", borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 }}>
                    <Text style={{ color: "#fff", fontSize: 11, fontWeight: "800" }}>#{row.ranking!.rank}</Text>
                  </View>
                )}
                {hasPerf && (
                  <Text style={{ fontSize: 12, color: "#6b7280" }}>
                    {row.performance!.runCount} run{row.performance!.runCount !== 1 ? "s" : ""}
                  </Text>
                )}
              </View>
              <Ionicons name={isOpen ? "chevron-up" : "chevron-down"} size={18} color="#6b7280" />
            </TouchableOpacity>

            {isOpen && (
              <View style={{ backgroundColor: "#ffffff", borderTopWidth: 1, borderTopColor: "#f3f4f6" }}>

                {/* Performance Stats — full-width, mirrors StatsCard from main
                    profile. Renders for any year w/ runs AND for the lifetime
                    row when the dog has run in multiple years (server returns
                    null performance for lifetime when dog is a rookie — only
                    runs in the current year — keeping just the Lifetime Ranking
                    visible per design). */}
                {hasPerf && (
                  <View style={{ paddingHorizontal: 20, paddingVertical: 16 }}>
                    <Text style={{ fontSize: 14, fontWeight: "700", color: "#374151", marginBottom: 12 }}>
                      {yearLabel} Performance Stats
                    </Text>
                    <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 10 }}>
                      <StatItem label="Total Runs" value={String(row.performance!.runCount)} />
                      <View style={{ width: 1, backgroundColor: "#f3f4f6" }} />
                      <StatItem label="Best Time" value={row.performance!.bestTime > 0 ? `${row.performance!.bestTime.toFixed(2)}s` : "—"} />
                    </View>
                    <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                      <StatItem label="Top Speed" value={`${row.performance!.bestSpeed.toFixed(2)} mph`} />
                      <View style={{ width: 1, backgroundColor: "#f3f4f6" }} />
                      <StatItem label="Total Points" value={row.performance!.totalPoints.toFixed(1)} />
                    </View>
                    {row.performance!.bestThreeAvgSpeed > 0 && row.performance!.runCount >= 3 && (
                      <View style={{ marginTop: 12, backgroundColor: "#f0fdf4", borderRadius: 8, padding: 10, flexDirection: "row", justifyContent: "center", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <Text style={{ fontSize: 14, color: "#15803d" }}>Best 3 Avg:</Text>
                        <Text style={{ fontSize: 14, fontWeight: "800", color: "#15803d" }}>{row.performance!.bestThreeAvgSpeed.toFixed(2)} mph</Text>
                        <Text style={{ fontSize: 13, color: "#16a34a" }}>(ranking speed)</Text>
                      </View>
                    )}
                    {row.performance!.titles.length > 0 && (
                      <View style={{ marginTop: 10, flexDirection: "row", justifyContent: "center", gap: 6, flexWrap: "wrap" }}>
                        {row.performance!.titles.map((t) => (
                          <View key={t} style={{ backgroundColor: "#1B2A4A", borderRadius: 4, paddingHorizontal: 8, paddingVertical: 4 }}>
                            <Text style={{ color: "#fff", fontSize: 12, fontWeight: "800", letterSpacing: 0.5 }}>{t}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                  </View>
                )}

                {row.year !== "lifetime" && !hasPerf && (
                  <View style={{ paddingHorizontal: 20, paddingVertical: 14 }}>
                    <Text style={{ fontSize: 14, fontWeight: "700", color: "#374151", marginBottom: 4 }}>{yearLabel} Performance Stats</Text>
                    <Text style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic" }}>No runs logged in Dash-Hound for {yearLabel}.</Text>
                  </View>
                )}
                {/* Lifetime row + rookie (no lifetime perf computed because all runs
                    are in the current year) — quietly skip the stats card, the
                    Lifetime Ranking below still shows. */}

                {/* AKC Top 5 — matches BreedStandings exactly: edge-to-edge, navy header, striped rows */}
                {hasRank && (
                  <View>
                    <View style={{ paddingHorizontal: 20, paddingTop: 14, paddingBottom: 10, borderTopWidth: 1, borderTopColor: "#f3f4f6" }}>
                      <Text style={{ fontSize: 14, fontWeight: "700", color: "#374151" }}>
                        AKC {yearLabel} Top 5
                      </Text>
                      <Text style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                        {row.ranking!.totalInBreed} {row.ranking!.totalInBreed === 1 ? "dog" : "dogs"} ranked in breed
                      </Text>
                    </View>
                    {/* Navy header row — same paddings as BreedStandings */}
                    <View style={{ flexDirection: "row", backgroundColor: "#1B2A4A", paddingVertical: 10, paddingHorizontal: 16 }}>
                      <Text style={{ width: 52, fontSize: 12, fontWeight: "700", color: "#ffffff" }}>#</Text>
                      <Text style={{ flex: 1, fontSize: 12, fontWeight: "700", color: "#ffffff" }}>Dog Name</Text>
                      <Text style={{ width: 65, fontSize: 12, fontWeight: "700", color: "#ffffff", textAlign: "right" }}>MPH</Text>
                    </View>
                    {/* Data rows */}
                    {(() => {
                      const inTop5 = row.ranking!.top5.some((r) => r.rank === row.ranking!.rank);
                      const rows = [...row.ranking!.top5];
                      if (!inTop5) {
                        rows.push({ rank: row.ranking!.rank, dogName: row.ranking!.akcDogName, speed: row.ranking!.speed });
                      }
                      return rows.map((r, i) => {
                        const isThisDog = r.rank === row.ranking!.rank;
                        const showEllipsis = !inTop5 && i === row.ranking!.top5.length;
                        return (
                          <View key={r.rank}>
                            {showEllipsis && (
                              <View style={{ paddingVertical: 4, alignItems: "center", backgroundColor: "#f9fafb" }}>
                                <Text style={{ fontSize: 14, color: "#9ca3af", letterSpacing: 4 }}>···</Text>
                              </View>
                            )}
                            <View style={{
                              flexDirection: "row",
                              paddingVertical: 12,
                              paddingHorizontal: 16,
                              backgroundColor: isThisDog ? "#FEF3C7" : (i % 2 === 0 ? "#ffffff" : "#f9fafb"),
                              borderLeftWidth: isThisDog ? 4 : 0,
                              borderLeftColor: isThisDog ? "#F59E0B" : "transparent",
                              alignItems: "center",
                            }}>
                              <Text style={{ width: 52, fontSize: 14, fontWeight: isThisDog ? "800" : "600", color: isThisDog ? "#1B2A4A" : "#374151" }} numberOfLines={1}>
                                #{r.rank}
                              </Text>
                              <View style={{ flex: 1, flexDirection: "row", alignItems: "center", gap: 6, overflow: "hidden" }}>
                                <Text style={{ flex: 1, fontSize: 14, fontWeight: isThisDog ? "800" : "500", color: isThisDog ? "#1B2A4A" : "#374151" }} numberOfLines={1}>
                                  {r.dogName}
                                </Text>
                                {isThisDog && (
                                  <View style={{ backgroundColor: "#F59E0B", paddingHorizontal: 6, paddingVertical: 1, borderRadius: 4 }}>
                                    <Text style={{ fontSize: 9, fontWeight: "800", color: "#ffffff" }}>YOU</Text>
                                  </View>
                                )}
                              </View>
                              <Text style={{ width: 65, fontSize: 14, fontWeight: isThisDog ? "800" : "600", color: isThisDog ? "#B45309" : "#F5A623", textAlign: "right" }}>
                                {r.speed != null ? r.speed.toFixed(2) : "—"}
                              </Text>
                            </View>
                          </View>
                        );
                      });
                    })()}
                  </View>
                )}
              </View>
            )}
          </View>
        );
      })}
    </View>
  );
}

function StatItem({ label, value }: { label: string; value: string }) {
  return (
    <View style={{ alignItems: "center", flex: 1 }}>
      <Text style={{ fontSize: 18, fontWeight: "800", color: "#F5A623" }}>{value}</Text>
      <Text style={{ fontSize: 12, color: "#6b7280", marginTop: 3, textAlign: "center" }}>{label}</Text>
    </View>
  );
}

